import React, { useState, useEffect, useRef } from 'react';
import { FAQS, QUICK_BUTTONS, FaqItem } from '../data/faqs';
import './Chatbot.css';

// ====== CONFIGURABLE ======
const WHATSAPP_NUMBER = "593000000000"; // Reemplaza con tu número sin "+" ni espacios. Ej: "593991112223"
const WHATSAPP_GREETING = "Hola, vengo del chatbot del sitio y necesito ayuda con: ";
const BOT_NAME = "Princonser Bot";

interface Message {
  text: string;
  from: 'user' | 'bot';
  timestamp: number;
}

const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Normalizar texto para búsqueda
  const normalize = (s: string): string => {
    return s.toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\p{L}\p{N}\s]/gu, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  // Calcular score de coincidencia
  const scoreQuery = (query: string, faq: FaqItem): number => {
    const qn = normalize(query);
    let score = 0;

    // direct include of FAQ question terms
    const qTerms = normalize(faq.q).split(" ");
    const qHits = qTerms.filter(t => t.length > 2 && qn.includes(t)).length;
    score += qHits / Math.max(3, qTerms.length);

    // tag hits
    const tagHits = faq.tags?.filter(t => qn.includes(normalize(t))).length || 0;
    score += tagHits / Math.max(2, faq.tags?.length || 1);

    return score;
  };

  // Encontrar mejor coincidencia
  const bestMatch = (query: string): { score: number; item: FaqItem | null } => {
    let best = { score: 0, item: null as FaqItem | null };

    FAQS.forEach(f => {
      const s = scoreQuery(query, f);
      if (s > best.score) best = { score: s, item: f };
    });

    return best;
  };

  // Top sugerencias
  const topSuggestions = (query: string, n: number = 3): string[] => {
    const arr = FAQS.map(f => ({ f, s: scoreQuery(query, f) }))
      .sort((a, b) => b.s - a.s);
    return arr.slice(0, n)
      .filter(x => x.s > 0.05)
      .map(x => x.f.q);
  };

  // Agregar mensaje
  const addMessage = (text: string, from: 'user' | 'bot') => {
    const newMessage: Message = {
      text,
      from,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, newMessage]);
    scrollToBottom();
  };

  // Scroll al final
  const scrollToBottom = () => {
    setTimeout(() => {
      if (bodyRef.current) {
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      }
    }, 50);
  };

  // Abrir WhatsApp
  const openWhatsApp = (context: string = '') => {
    const text = encodeURIComponent(WHATSAPP_GREETING + context);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
    window.open(url, "_blank", "noopener");
  };

  // Responder del bot
  const botAnswer = async (userText: string) => {
    setIsTyping(true);
    
    // Simular latencia
    await new Promise(r => setTimeout(r, 600));
    
    setIsTyping(false);
    
    const { score, item } = bestMatch(userText);

    if (item && score >= 0.35) {
      if (item.a) {
        addMessage(item.a, 'bot');
      } else {
        // This is the "human" path
        addMessage("Puedo ponerte en contacto ahora mismo por WhatsApp con nuestro equipo. ¿Deseas continuar?", 'bot');
        
        // Agregar botones de acción
        setTimeout(() => {
          const actionsDiv = document.createElement('div');
          actionsDiv.className = 'pk-actions';
          actionsDiv.innerHTML = `
            <button class="pk-chip" data-ok="1">Sí, abrir WhatsApp</button>
            <button class="pk-chip" data-ok="0">No, seguir aquí</button>
          `;
          
          const wrap = document.createElement('div');
          wrap.className = 'pk-msg from-bot';
          const bubb = document.createElement('div');
          bubb.className = 'bubble';
          bubb.appendChild(actionsDiv);
          wrap.appendChild(bubb);
          
          if (bodyRef.current) {
            bodyRef.current.appendChild(wrap);
            scrollToBottom();
          }

          actionsDiv.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            const ok = target.getAttribute('data-ok');
            if (ok === '1') {
              openWhatsApp(userText);
            } else {
              addMessage("Perfecto, seguimos por aquí. ¿Qué más te gustaría saber?", 'bot');
            }
          }, { once: true });
        }, 100);
      }
    } else {
      const sugg = topSuggestions(userText, 3);
      addMessage("No estoy 100% seguro de la respuesta. ¿Quieres hablar con un asesor por WhatsApp? Puedo abrir la conversación con un clic.", 'bot');
      
      setTimeout(() => {
        const actionsDiv = document.createElement('div');
        actionsDiv.className = 'pk-actions';
        let html = `<button class="pk-chip" data-act="whatsapp">Abrir WhatsApp</button>`;
        
        if (sugg.length) {
          html += sugg.map(s => `<button class="pk-chip" data-suggest="${s.replace(/"/g, '&quot;')}">${s}</button>`).join('');
        }
        
        actionsDiv.innerHTML = html;
        
        const wrap = document.createElement('div');
        wrap.className = 'pk-msg from-bot';
        const bubb = document.createElement('div');
        bubb.className = 'bubble';
        bubb.appendChild(actionsDiv);
        wrap.appendChild(bubb);
        
        if (bodyRef.current) {
          bodyRef.current.appendChild(wrap);
          scrollToBottom();
        }

        actionsDiv.addEventListener('click', (e) => {
          const target = e.target as HTMLElement;
          const act = target.getAttribute('data-act');
          const suggQ = target.getAttribute('data-suggest');
          
          if (act === 'whatsapp') {
            openWhatsApp(userText);
          }
          if (suggQ) {
            handleQuickButton(suggQ);
          }
        }, { once: true });
      }, 100);
    }
  };

  // Manejar botón rápido (FAQ)
  const handleQuickButton = (question: string) => {
    addMessage(question, 'user');
    botAnswer(question);
  };

  // Mostrar acciones rápidas
  const showQuickActions = () => {
    const actionsDiv = document.createElement('div');
    actionsDiv.className = 'pk-actions';
    
    // Botones de FAQs
    const faqButtons = QUICK_BUTTONS.map(q => 
      `<button class="pk-chip" data-faq="${q.replace(/"/g, '&quot;')}">${q}</button>`
    ).join('');
    
    actionsDiv.innerHTML = `
      ${faqButtons}
      <button class="pk-chip" data-act="whatsapp">Hablar por WhatsApp</button>
    `;
    
    const wrap = document.createElement('div');
    wrap.className = 'pk-msg from-bot';
    const bubb = document.createElement('div');
    bubb.className = 'bubble';
    bubb.appendChild(actionsDiv);
    wrap.appendChild(bubb);
    
    if (bodyRef.current) {
      bodyRef.current.appendChild(wrap);
      scrollToBottom();
    }

    actionsDiv.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const faq = target.getAttribute('data-faq');
      const act = target.getAttribute('data-act');
      
      if (faq) {
        handleQuickButton(faq);
      }
      if (act === 'whatsapp') {
        openWhatsApp("Me gustaría hablar con un asesor.");
      }
    });
  };

  // Enviar mensaje
  const handleSend = () => {
    const txt = inputValue.trim();
    if (!txt) return;

    addMessage(txt, 'user');
    setInputValue('');
    botAnswer(txt);
  };

  // Toggle chat
  const toggleChat = (show: boolean) => {
    setIsOpen(show);
    if (show) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
        scrollToBottom();
      }, 50);

      if (!sessionStorage.getItem("pk_welcomed")) {
        addMessage(`¡Hola! Soy ${BOT_NAME}. Puedo responder preguntas frecuentes y, si lo prefieres, te conecto por WhatsApp con nuestro equipo.`, 'bot');
        setTimeout(() => {
          showQuickActions();
        }, 300);
        sessionStorage.setItem("pk_welcomed", "1");
      }
    }
  };

  // Efecto para manejar tecla Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        toggleChat(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Efecto para scroll automático
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  return (
    <>
      {/* Launcher */}
      <div
        className="pk-launcher"
        id="pkLauncher"
        onClick={() => toggleChat(!isOpen)}
        role="button"
        aria-haspopup="dialog"
        aria-controls="pkChat"
        aria-expanded={isOpen}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleChat(!isOpen);
          }
        }}
      >
        <div className="pk-avatar" aria-hidden="true">
          <span>P</span>
        </div>
        <div className="pk-label">¿Te ayudo?</div>
      </div>

      {/* Chat Window */}
      <section
        className={`pk-chat ${isOpen ? 'show' : ''}`}
        id="pkChat"
        role="dialog"
        aria-labelledby="pkTitle"
        aria-modal="true"
      >
        <header className="pk-header">
          <div className="pk-avatar" aria-hidden="true">
            <span>P</span>
          </div>
          <div>
            <div className="title" id="pkTitle">{BOT_NAME}</div>
            <div className="subtitle">FAQ y soporte inmediato</div>
          </div>
          <button
            className="pk-close"
            id="pkClose"
            onClick={() => toggleChat(false)}
            aria-label="Cerrar"
          >
            ×
          </button>
        </header>

        <div className="pk-body" id="pkBody" ref={bodyRef} tabIndex={0} aria-live="polite">
          {messages.map((msg, idx) => (
            <div key={idx} className={`pk-msg ${msg.from === 'user' ? 'from-user' : 'from-bot'}`}>
              <div className="bubble">{msg.text}</div>
            </div>
          ))}
          {isTyping && (
            <div className="pk-msg from-bot pk-typing-wrap">
              <div className="bubble">
                <div className="pk-typing" aria-label="Escribiendo…">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="pk-input">
          <label htmlFor="pkText" className="sr-only">
            Escribe tu mensaje
          </label>
          <input
            id="pkText"
            ref={inputRef}
            type="text"
            placeholder="Escribe tu pregunta…"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSend();
              }
            }}
            autoComplete="off"
          />
          <button id="pkSend" onClick={handleSend} aria-label="Enviar">
            Enviar
          </button>
        </div>

        <div className="pk-footnote">
          Sugerencia: prueba con "¿Qué incluye el sistema?", "¿Para qué edades?" o "Hablar con humano".
        </div>
      </section>
    </>
  );
};

export default Chatbot;

